import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Edit3, Trash2, Save, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";
const labelCls = "block text-xs font-medium text-slate-700 mb-1.5";

const BlogForm = ({ initial, onCancel, onSaved }) => {
  const [post, setPost] = useState({
    title: initial?.title || "",
    slug: initial?.slug || "",
    cover_image: initial?.cover_image || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    published: initial?.published ?? false,
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!post.title) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      const payload = { ...post, slug: post.slug || null };
      if (isEdit) {
        await api.patch(`/admin/blogs/${initial.id}`, payload, { withCredentials: true });
        toast.success("Post updated.");
      } else {
        await api.post("/admin/blogs", payload, { withCredentials: true });
        toast.success("Post created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/40 rounded-lg p-5 shadow-card-hover space-y-4" data-testid="blog-form">
      <div className="font-semibold text-slate-900">{isEdit ? `Edit · ${initial.slug}` : "New blog post"}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Title</label>
          <input data-testid="blog-input-title" className={inputCls} value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Slug (auto from title if blank)</label>
          <input data-testid="blog-input-slug" className={inputCls} value={post.slug} onChange={(e) => setPost({ ...post, slug: e.target.value })} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Cover image URL</label>
        <input data-testid="blog-input-cover" className={inputCls} value={post.cover_image} onChange={(e) => setPost({ ...post, cover_image: e.target.value })} />
      </div>
      <div>
        <label className={labelCls}>Excerpt</label>
        <textarea data-testid="blog-input-excerpt" rows={2} className={`${inputCls} resize-none`} value={post.excerpt} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} />
      </div>
      <div>
        <label className={labelCls}>Content (markdown / HTML)</label>
        <textarea data-testid="blog-input-content" rows={10} className={`${inputCls} resize-y font-mono text-xs`} value={post.content} onChange={(e) => setPost({ ...post, content: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" data-testid="blog-input-published" checked={post.published} onChange={(e) => setPost({ ...post, published: e.target.checked })} className="w-4 h-4 accent-spike-blue" />
        <span className="text-sm text-slate-700">Published</span>
      </label>
      <div className="flex gap-2">
        <button type="button" data-testid="blog-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button type="button" data-testid="blog-cancel" onClick={onCancel} className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-4 py-2 rounded-md">Cancel</button>
      </div>
    </div>
  );
};

const AdminBlogs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blogs", { withCredentials: true });
      setItems(data);
    } catch {
      toast.error("Could not load blogs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blogs/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-blogs">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-spike-blue" /> Blog Posts
          </h2>
          <p className="text-sm text-slate-600 mt-1">Manage public blog content. Posts marked Published appear on the website.</p>
        </div>
        {!creating && !editing && (
          <button type="button" data-testid="blog-add-new" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4" /> New post
          </button>
        )}
      </div>

      {creating && <BlogForm initial={null} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-card" data-testid={`blog-card-${p.id}`}>
              {editing === p.id ? (
                <BlogForm initial={p} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
              ) : (
                <div className="flex justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{p.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {p.published ? "Published" : "Draft"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">/{p.slug} · updated {new Date(p.updated_at).toLocaleDateString()}</div>
                    {p.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.excerpt}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`blog-edit-${p.id}`} onClick={() => setEditing(p.id)} className="p-2 text-slate-600 hover:text-spike-blue rounded">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button type="button" data-testid={`blog-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-slate-600 hover:text-red-500 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="blog-empty">
              No posts yet. Create your first one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBlogs;
